import {
  getAcademicYears,
  getBillingCenters,
  getBillingMonths,
  getBillingProviders,
  getBillingProvidersByServiceType,
  getBillingStatus,
  getBillingServiceDetails,
  getVisitDetails,
  getAdditionalInfo,
  getThrpyBillingAdjustments,
  getBillingStatusTransition,
  getBillingDetailServiceType,
  getBillingDetailDiscipline,
  getBillingDetailSpecialist,
  checkUncheckFlagServiceDetail,
  getBillingHistory,
  getBillingAdjustmentDetails,
  getReasons,
  getVisitByService,
  saveAdjustment,
  getBillingServiceAdjustments,
  getServiceTypes,
  getInterventors as _getInterventors,
} from '../api';
import _certLoadService from '@/modules/certificationLoad/api.js';
import actionTypes from './types/action.types';
import mutationTypes from './types/mutation.types';
import billingLevel from '@/utils/constants/billingLevel';

const actions = {
  async [actionTypes.GET_ACADEMIC_YEARS]({
    commit,
    state,
    getters,
    rootGetters,
  }) {
    const minYearId = getters.getMinYearId;
    const maxYearId =
      rootGetters['$_common/currentAcademicYear'] != null
        ? rootGetters['$_common/currentAcademicYear'].id
        : null;
    if (state.catalog.academicYears.length == 0) {
      const data = await getAcademicYears(minYearId, maxYearId);
      commit(mutationTypes.SET_ACADEMIC_YEARS, data);
    }
  },
  async [actionTypes.GET_MONTHS]({ commit }, academicYearId) {
    const { mappedMonths } = await getBillingMonths(academicYearId);
    commit(mutationTypes.SET_MONTHS, mappedMonths);
  },
  async [actionTypes.GET_CENTERS]({ commit, state }) {
    if (state.catalog.centers.length == 0) {
      const { data } = await getBillingCenters();
      commit(mutationTypes.SET_CENTERS, data);
    }
  },
  async [actionTypes.GET_PROVIDERS]({ commit }, academicYearId) {
    const { data } = await getBillingProviders(academicYearId);
    commit(mutationTypes.SET_PROVIDERS, data);
  },
  async [actionTypes.GET_PROVIDERS_SERVICE_TYPE](
    { commit },
    { academicYearId, serviceTypeId }
  ) {
    const { data } = await getBillingProvidersByServiceType(
      academicYearId,
      serviceTypeId
    );
    commit(mutationTypes.SET_PROVIDERS_SERVICE_TYPE, data);
  },
  async [actionTypes.GET_STATUS]({ commit, getters, state }) {
    if (state.catalog.status.length == 0) {
      const data = await getBillingStatus(getters.canViewOnlyPreliminarStatus);
      commit(mutationTypes.SET_STATUS, data);
    }
  },
  async [actionTypes.GET_SERVICE_TYPES]({ commit, state }) {
    if (state.catalog.serviceTypes.length == 0) {
      const { data } = await getServiceTypes();
      commit(mutationTypes.SET_SERVICE_TYPES, data);
    }
  },
  async [actionTypes.GET_INTERVENTORS]({ commit, state }) {
    if (state.catalog.interventors.length == 0) {
      const { data } = await _getInterventors();
      commit(mutationTypes.SET_INTERVENTORS, data);
    }
  },
  async [actionTypes.GET_BILLING_SERVICE_DETAILS](
    { commit, getters },
    payload
  ) {
    const { initialPageSize } = getters.getBillingResultsPaginationConfig;
    const { data } = await getBillingServiceDetails({
      ...payload,
      pageIndex: payload.pageIndex * initialPageSize,
    });
    commit(mutationTypes.SET_BILLING_SERVICE_DETAILS, data);
    commit(mutationTypes.SET_BILLING_SERVICE_DETAILS_PAGINATION, payload);
  },
  async [actionTypes.GET_VISIT_DETAILS]({ commit }, params) {
    const { data } = await getVisitDetails(params);
    commit(mutationTypes.SET_VISIT_DETAILS, { payload: data, item: params });
  },
  async [actionTypes.GET_BILLING_ADDITIONAL_INFO](
    { commit },
    { type, id, itemIndex }
  ) {
    const { data } = await getAdditionalInfo(type, id);
    commit(mutationTypes.SET_BILLING_ADDITIONAL_INFO, { itemIndex, data });
  },
  async [actionTypes.GET_BILLING_VISIT_CERT_DOCUMENT_TYPES](
    { commit },
    { itemIndex }
  ) {
    const { data } = await _certLoadService.getDocumentTypes();
    commit(mutationTypes.SET_BILLING_VISIT_CERT_DOCUMENT_TYPES, {
      itemIndex,
      payload: data,
    });
  },
  async [actionTypes.UPLOAD_VISIT_CERT_DOCUMENT](_, payload) {
    const { data } = await _certLoadService.uploadFiles(payload.formData);
    return data;
  },
  async [actionTypes.GET_BILLING_ADJUSTMENTS_DETAILS](
    { commit },
    { id, itemIndex }
  ) {
    const { data } = await getThrpyBillingAdjustments(id);
    commit(mutationTypes.SET_BILLING_ADJUSTMENTS_DETAILS, {
      payload: data,
      itemIndex,
    });
  },
  async [actionTypes.GET_BILLING_STATUS_TRANSITION](
    { commit },
    { statusId, billId }
  ) {
    const { data } = await getBillingStatusTransition(statusId, billId);
    commit(mutationTypes.SET_BILLING_STATUS_TRANSITION, data);
  },
  async [actionTypes.GET_BILLING_DETAILS_SERVICE_TYPE]({ commit }, { billId }) {
    const { data } = await getBillingDetailServiceType(billId);
    commit(mutationTypes.SET_BILLING_DETAILS_SERVICE_TYPE, data);
  },
  async [actionTypes.GET_BILLING_DETAILS_DISCIPLINE](
    { commit },
    { billId, serviceTypeId }
  ) {
    const { data } = await getBillingDetailDiscipline(billId, serviceTypeId);
    commit(mutationTypes.SET_BILLING_DETAILS_DISCIPLINE, data);
  },
  async [actionTypes.GET_BILLING_DETAILS_SPECIALIST]({ commit }, { billId }) {
    const { data } = await getBillingDetailSpecialist(billId);
    commit(mutationTypes.SET_BILLING_DETAILS_SPECIALIST, data);
  },
  async [actionTypes.CHECK_UNCHECK_FLAG_SERVICE_DETAIL](
    { commit },
    { payload, checked, checkedAll, itemIndex }
  ) {
    const { data } = await checkUncheckFlagServiceDetail(payload);
    if (data) {
      commit(mutationTypes.SET_CHECK_UNCHECK_FLAG_SERVICE_DETAIL, {
        checked,
        checkedAll,
        itemIndex,
      });
    }
  },
  async [actionTypes.GET_BILLING_TRACKING]({ commit }, billId) {
    const { data } = await getBillingHistory(billId);
    commit(mutationTypes.SET_BILLING_TRACKING, data);
  },
  async [actionTypes.GET_BILLING_ADJUSTMENTS_DETAILS_LIST](
    { commit },
    { billId, payload }
  ) {
    const { data } = await getBillingAdjustmentDetails(billId, payload);
    commit(mutationTypes.SET_BILLING_ADJUSTMENTS_DETAILS_LIST, data);
  },
  //Manual Adjustment
  async [actionTypes.GET_REASONS]({ commit }, payload) {
    const reasons = await getReasons(payload);
    if (reasons.data) {
      commit(mutationTypes.SET_REASONS, reasons.data);
    }
    if (
      payload.typeId == billingLevel.visit ||
      payload.typeId == billingLevel.educational
    ) {
      const { data } = await getVisitByService(payload);
      commit(mutationTypes.SET_VISITS, data);
    }
  },
  async [actionTypes.SAVE_ADJUSTMENT](context, payload) {
    let result = await saveAdjustment(payload);
    return result;
  },
  async [actionTypes.GET_BILLING_SERVICE_ADJUSTMENTS](
    { commit },
    { billId, id, itemIndex }
  ) {
    const { data } = await getBillingServiceAdjustments({ billId, id });
    commit(mutationTypes.SET_BILLING_SERVICE_ADJUSTMENTS, {
      payload: data,
      itemIndex,
    });
  },
};

export default actions;
